import { initializeAppCheck, getToken, ReCaptchaV3Provider } from "firebase/app-check";
import {
  logSentryException
} from "../utils/sentryEventLogger";


const { app } = require('./firesBaseConfig');

// Constants
let MAX_RETRIES = 6;
const RETRY_DELAY_MS = 1000;
let currentDialog = false;
const PRESERVED_ITEMS = ["auth", "facilities", "selectedFacility", "fcmDeviceToken", "appCheckTokenFailed"];

// Initialize App Check if environment is staging or prod
let appCheck;
if (process.env.REACT_APP_ENV === 'staging' || process.env.REACT_APP_ENV === 'prod') {
  appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(process.env.REACT_APP_RECAPTCHA_KEY),
  });
}

function deleteIndexedDatabase(dbName) {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.deleteDatabase(dbName);
    request.onsuccess = () => resolve();
    request.onerror = (event) => reject(event.target.error);
    request.onblocked = () => {
      resolve();
    };
  });
}

const getUserDetails = localStorage.getItem("auth");
const userPhone = getUserDetails ? JSON.parse(getUserDetails)?.phone_number : null;
const fullName = getUserDetails ? JSON.parse(getUserDetails)?.name : null;

const clearSiteData = async () => {
  // const err = new Error("App Check token retrieval failed");

  // Preserve necessary items
  const preservedItems = {};
  PRESERVED_ITEMS.forEach(key => {
    if (localStorage.getItem(key)) {
      preservedItems[key] = localStorage.getItem(key);
    }
  });

  // Clear storage
  localStorage.clear();
  sessionStorage.clear();

  // Clear cookies
  document.cookie.split(";").forEach(cookie => {
    const name = cookie.split("=")[0].trim();
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  });

  try {
    const databases = await window.indexedDB.databases();
    databases.forEach(db => {
      deleteIndexedDatabase(db.name);
    });
  } catch (error) {
    console.error('Error deleting database:', error);
  }
  // Unregister service workers
  if ("serviceWorker" in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
    } catch (error) {
      console.error('Error unregistering service workers:', error);
    }
  }

  // Restore preserved items
  Object.keys(preservedItems).forEach(key => {
    localStorage.setItem(key, preservedItems[key]);
  });

  // Set Custom Sentry Error
  logSentryException({
    err: new Error("AppCheck Token failed"),
    phoneNumber: userPhone,
    userName: fullName,
    customTags: { page: "AppCheck", type: "appcheck_token_error" },
  });

  window.location.reload();
};

const handleRetry = () => {
  clearSiteData();
};

const showCustomDialog = (
  message = "Failed to verify app security. Please try again.") => new Promise((resolve) => {
    if (currentDialog) return;
    currentDialog = true;

    const dialogOverlay = document.createElement('div');

    dialogOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      font-family: inherit;
      align-items: center;
      justify-content: center;
      z-index: 9999;
    `;

    const dialogBox = document.createElement('div');
    dialogBox.style.cssText = `
      background-color: white;
      border-radius: 8px;
      padding: 24px;
      font-family: inherit;
      max-width: 400px;
      width: 90%;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    `;

    const header = document.createElement('h3');
    header.textContent = 'Connection Error';
    header.style.cssText = `
      margin-top: 0;
      margin-bottom: 3px;
      color: #333;
      font-family: inherit;
      font-size: 18px;
    `;

    const messageEl = document.createElement('p');
    messageEl.textContent = message;
    messageEl.style.cssText = `
      margin-bottom: 15px;
      color: #555;
      font-family: inherit;
      font-size: 16px;
    `;

    const buttonsContainer = document.createElement('div');
    buttonsContainer.style.cssText = `
      display: flex;
      justify-content: flex-end;
      gap: 12px;
    `;

    const retryButton = document.createElement('button');
    retryButton.textContent = 'Retry';
    retryButton.style.cssText = `
      padding: 8px 16px;
      border-radius: 4px;
      border: none;
      background-color: rgb(255, 136, 98);
      color: white;
      font-family: inherit;
      cursor: pointer;
      font-size: 14px;
      z-index: 9999;
    `;
    retryButton.addEventListener('click', () => {
      resolve(true);
      handleRetry();
    });

    buttonsContainer.appendChild(retryButton);
    dialogBox.appendChild(header);
    dialogBox.appendChild(messageEl);
    dialogBox.appendChild(buttonsContainer);
    dialogOverlay.appendChild(dialogBox);
    document.body.appendChild(dialogOverlay);
    retryButton.focus();
  });

export const getAppCheckToken = async () => {
  if (!appCheck) {
    console.error("App Check is not initialized - clearing site data and skipping token retrieval");
    await clearSiteData();
    return null;
  }

  const hasPreviouslyFailed = localStorage.getItem('appCheckTokenFailed');
  let appCheckTokenResponse = null;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      // eslint-disable-next-line no-await-in-loop
      appCheckTokenResponse = await getToken(appCheck, false);

      if (appCheckTokenResponse) {
        localStorage.setItem("appCheckTokenFailed", "false");
        return appCheckTokenResponse;
      }


    } catch (err) {

      if (hasPreviouslyFailed === "true") {

        MAX_RETRIES = 1;
        showCustomDialog("Failed to verify app security. Please try again.");
        return null;
      }
      console.error(`Token retrieval attempt ${attempt} failed:`, err);
    }

    if (attempt < MAX_RETRIES) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => { setTimeout(resolve, RETRY_DELAY_MS) });
    }
  }

  if (!appCheckTokenResponse) {
    clearSiteData();
    localStorage.setItem("appCheckTokenFailed", "true");
  }

  return appCheckTokenResponse;
};